import { CarePartnerCreateDTO, CarePartnersService } from '../client'
import { CarePartnerPublicResponseDTO } from '../client/models/CarePartnerPublicResponseDTO'
import { CarePartnerResponseDTO } from '../client/models/CarePartnerResponseDTO'
import { PaginatedDTO_CarePartnerResponseDTO_ } from '../client/models/PaginatedDTO_CarePartnerResponseDTO_'
import { PaginatedStateAdapter, usePaginatedData } from './usePaginatedData'

type ListCarePartnersParams = Parameters<typeof CarePartnersService.listCarePartnersCarePartnersGet>[0]
export type CarePartnerStatusFilter = NonNullable<ListCarePartnersParams['status']>[number]

/**
 * ``usePaginatedData`` holds filters as a flat Record<string, string>, so a
 * multi-select status reaches this hook comma-joined rather than as an array.
 * Expand it here rather than changing the shared hook's state shape for its
 * other consumers — the same approach `useCandidates` takes.
 *
 * The cast is unavoidable: the union of valid statuses cannot survive a trip
 * through a stringly-typed filter map. Values come from the status dropdown,
 * and the backend rejects anything outside the enum with a 422.
 */
const fetchCarePartnersPage = ({ status, ...rest }: Omit<ListCarePartnersParams, 'status'> & { status?: string | string[] }) =>
    CarePartnersService.listCarePartnersCarePartnersGet({
        ...rest,
        status: (typeof status === 'string' ? status.split(',').filter(Boolean) : status) as CarePartnerStatusFilter[] | undefined
    })

export const useCarePartners = (
    initialPage = 1,
    initialLimit = 10,
    initialSort = 'first_name:asc',
    initialFilters = {},
    stateAdapter?: PaginatedStateAdapter
) => {
    // Note: createCarePartnerCarePartnersPost returns CarePartnerPublicResponseDTO | CarePartnerResponseDTO
    // but usePaginatedData expects a single type. We'll use CarePartnerResponseDTO as the base type
    // and handle the union type in the actual usage.
    const { refetch, ...rest } = usePaginatedData<
        PaginatedDTO_CarePartnerResponseDTO_,
        CarePartnerCreateDTO,
        CarePartnerResponseDTO | CarePartnerPublicResponseDTO
    >(
        fetchCarePartnersPage,
        initialPage,
        initialLimit,
        initialSort,
        initialFilters,
        CarePartnersService.createCarePartnerCarePartnersPost as any, // Type assertion needed due to union return type
        stateAdapter
    )
    return { ...rest, refetch }
}
